import { Injectable, signal, effect } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export type Language = 'en' | 'lo';

export interface LanguageConfig {
  code: Language;
  name: string;
  nativeName: string;
  flag: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly STORAGE_KEY = 'insee_ihub_language';
  
  // Available languages
  readonly languages: LanguageConfig[] = [
    { code: 'lo', name: 'Lao', nativeName: 'ລາວ', flag: '🇱🇦' },
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' }
  ];
  
  // Current language signal
  currentLanguage = signal<Language>('lo');
  
  constructor(private translate: TranslateService) {
    // Initialize language from localStorage or default
    const savedLang = this.loadLanguagePreference();
    this.setLanguage(savedLang);
    
    // Save language preference whenever it changes
    effect(() => {
      const lang = this.currentLanguage();
      this.saveLanguagePreference(lang);
    });
  }
  
  /**
   * Set the current language
   */
  setLanguage(lang: Language): void {
    this.translate.use(lang);
    this.currentLanguage.set(lang);
    this.updateHtmlLang(lang);
  }
  
  /**
   * Toggle between available languages
   */
  toggleLanguage(): void {
    const newLang: Language = this.currentLanguage() === 'en' ? 'lo' : 'en';
    this.setLanguage(newLang);
  }
  
  /**
   * Get language config by code
   */
  getLanguageConfig(code: Language): LanguageConfig | undefined {
    return this.languages.find(lang => lang.code === code);
  }
  
  /**
   * Get current language config
   */
  getCurrentLanguageConfig(): LanguageConfig {
    return this.getLanguageConfig(this.currentLanguage()) || this.languages[0];
  }
  
  /**
   * Check if a language is currently active
   */
  isLanguageActive(code: Language): boolean {
    return this.currentLanguage() === code;
  }
  
  /**
   * Load language preference from localStorage
   */
  private loadLanguagePreference(): Language {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved === 'en' || saved === 'lo') {
        return saved;
      }
    } catch (error) {
      console.warn('Failed to load language preference:', error);
    }
    return 'lo'; // Default to Lao
  }
  
  /**
   * Save language preference to localStorage
   */
  private saveLanguagePreference(lang: Language): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, lang);
    } catch (error) {
      console.warn('Failed to save language preference:', error);
    }
  }
  
  /**
   * Update HTML lang attribute and font for accessibility
   */
  private updateHtmlLang(lang: Language): void {
    try {
      document.documentElement.lang = lang;

      // Use boonhome font for both languages
      const fontFamily = "'boonhome', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

      document.documentElement.style.fontFamily = fontFamily;
      document.body.style.fontFamily = fontFamily;
    } catch (error) {
      console.warn('Failed to update HTML lang attribute or font:', error);
    }
  }
  
  /**
   * Get translated text with bilingual fallback
   * Useful for API responses that have both lo and en fields
   */
  getBilingualText(textLo?: string, textEn?: string): string {
    const currentLang = this.currentLanguage();
    if (currentLang === 'lo' && textLo) {
      return textLo;
    }
    if (currentLang === 'en' && textEn) {
      return textEn;
    }
    // Fallback: return whatever is available
    return textLo || textEn || '';
  }
}
